<template>
    <v-container>
        <v-overlay color="iq_dark" :value="loading">
            <v-icon color="iq_light" x-large>mdi-loading mdi-spin</v-icon>
            <span class="iq_light--text font-weight-bold">Loading Validate IQ...</span>
        </v-overlay>

        <v-dialog v-model="showUnsavedChangesConfirm" max-width="420" persistent>
            <v-card>
                <v-card-title>Unsaved Changes</v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    You have unsaved changes to your Phone Properties to Validate or Phone Formatting Options. They
                    won't be saved unless you go back and click Save on that card. Continue anyway?
                </v-card-text>
                <v-card-actions class="px-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelPendingAction">Cancel</v-btn>
                    <v-btn color="primary" @click="confirmPendingAction">Continue</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="autoRenewConfirm.show" max-width="480" persistent>
            <v-card class="rounded-lg overflow-hidden">
                <div class="d-flex align-center px-6 pt-6 pb-3">
                    <v-avatar :color="autoRenewConfirm.iconBg" size="46" class="mr-4">
                        <v-icon dark>{{ autoRenewConfirm.icon }}</v-icon>
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.25;">{{ autoRenewConfirm.title }}</div>
                        <div v-if="autoRenewConfirm.subtitle" class="body-2 grey--text text--darken-1">{{ autoRenewConfirm.subtitle }}</div>
                    </div>
                </div>
                <v-card-text class="pt-2 pb-0">
                    <v-sheet v-if="autoRenewConfirm.rows.length" outlined rounded class="px-4 py-1">
                        <template v-for="(row, index) in autoRenewConfirm.rows">
                            <v-divider v-if="index > 0" :key="'divider-' + index"></v-divider>
                            <div :key="'row-' + index" class="d-flex align-center justify-space-between py-3">
                                <span class="body-2 grey--text text--darken-1">{{ row.label }}</span>
                                <span :class="row.emphasis ? 'text-h6 font-weight-bold primary--text' : 'body-2 font-weight-medium'">{{ row.value }}</span>
                            </div>
                        </template>
                    </v-sheet>
                    <v-alert
                        v-for="(note, index) in autoRenewConfirm.notes"
                        :key="'note-' + index"
                        :type="note.type"
                        text
                        dense
                        class="body-2 mb-0"
                        :class="index > 0 || autoRenewConfirm.rows.length ? 'mt-3' : ''"
                    >{{ note.text }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-6 py-5">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelAutoRenewConfirm">Cancel</v-btn>
                    <v-btn color="primary" depressed large class="px-6" @click="confirmAutoRenewChange">{{ autoRenewConfirm.confirmLabel }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000" top>
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
            </template>
        </v-snackbar>

        <v-row>
            <v-col cols="12" class="d-flex align-center">
                <img src="@/assets/images/logo-validateiq-pink.png" alt="Validate IQ" height="48" class="mr-3">
                <div>
                    <h1>Validate IQ</h1>
                    <p class="mb-0">Validate HubSpot contact phone numbers with configurable formatting options.</p>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="!loading && !status.hubspotConnected" justify="center">
            <v-col cols="12" md="9" lg="7">
                <v-card outlined class="text-center">
                    <v-card-text class="pt-8 pb-6">
                        <v-avatar color="primary lighten-5" size="72" class="mb-4">
                            <v-icon color="primary" size="36">$hubspotIcon</v-icon>
                        </v-avatar>
                        <h2 class="mb-2">Connect to HubSpot</h2>
                        <p class="grey--text text--darken-1 mb-0">
                            Connect your HubSpot account so Validate IQ can check and format your contacts' phone numbers.
                        </p>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-text class="text-left">
                        <p class="mb-4">
                            Connecting creates a <strong>Validate IQ</strong> property group on the HubSpot
                            <strong>Contact</strong> object, with the properties below. Phone and Mobile Phone
                            validation are on by default - you can turn either off, or add up to 3 custom
                            properties, from the settings once you're connected.
                        </p>

                        <v-row>
                            <v-col
                                v-for="group in hubspotDefaultPropertyGroups"
                                :key="group.label"
                                cols="12"
                                sm="6"
                            >
                                <div class="font-weight-medium mb-1">{{ group.label }}</div>
                                <v-list dense class="py-0">
                                    <v-list-item
                                        v-for="property in group.properties"
                                        :key="property.label"
                                        class="px-0"
                                    >
                                        <v-list-item-icon class="mr-2 my-2 align-self-start">
                                            <v-icon small color="primary">mdi-tag-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content class="py-1">
                                            <div class="text-body-2 font-weight-medium">{{ property.label }}</div>
                                            <div class="caption grey--text text--darken-1">{{ property.description }}</div>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-col>
                        </v-row>

                        <p class="caption grey--text text--darken-1 mt-2 mb-0">
                            Adding a custom property to validate later creates this same set of properties named after it,
                            e.g. Validate IQ Another Phone Status for a custom property labeled "Another Phone".
                        </p>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions class="px-4 py-4 d-flex flex-column align-center">
                        <v-btn color="primary" large :loading="connectingToHubSpot" @click="connectHubSpot">
                            <v-icon class="mr-2">$hubspotIcon</v-icon>
                            Connect HubSpot
                        </v-btn>
                        <div class="caption grey--text text--darken-1 mt-2 text-center">
                            This connects your HubSpot account - required before Validate IQ can start validating phone numbers.
                        </div>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="!loading && status.hubspotConnected && !status.propertiesCreated">
            <v-col cols="12">
                <v-card outlined>
                    <v-card-title>Sync Validate IQ Properties</v-card-title>
                    <v-card-subtitle>
                        HubSpot is connected, but Validate IQ properties are not ready yet.
                    </v-card-subtitle>
                    <v-divider></v-divider>
                    <v-card-actions class="px-4">
                        <v-btn color="primary" :loading="syncingProperties" @click="syncProperties">
                            <v-icon class="mr-2">mdi-sync</v-icon>
                            Sync Contact Properties
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <template v-if="!loading && status.hubspotConnected && status.propertiesCreated">
            <v-tabs v-model="activeTab" class="mb-4">
                <v-tab>Validate</v-tab>
                <v-tab>Settings</v-tab>
                <v-tab>Billing</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
            <v-tab-item>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="d-flex flex-column fill-height">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Validate Specific Contact</span>
                            <v-btn icon small @click="showSingleContactHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Validate one contact by id or URL.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text class="flex-grow-1">
                            <v-text-field
                                v-model="singleContactId"
                                label="HubSpot contact id or contact URL"
                                hide-details
                                dense
                            ></v-text-field>
                            <v-alert v-if="singleContactResult" :type="singleContactResult.success ? 'success' : 'error'" dense outlined class="mb-0 mt-4">
                                {{ singleContactResult.success ? 'Contact validation completed.' : singleContactResult.errorMessage || 'Contact validation failed.' }}
                            </v-alert>
                        </v-card-text>
                        <v-card-actions class="px-4">
                            <v-btn color="primary" :loading="validatingSingleContact" @click="guardedValidateSingleContact">
                                Validate Contact
                            </v-btn>
                        </v-card-actions>
                    </v-card>

                    <v-dialog v-model="showSingleContactHelp" max-width="500">
                        <v-card>
                            <v-card-title>Validate Specific Contact</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Validates all of your configured phone properties - Phone, Mobile Phone, and any custom
                                    properties - for a single HubSpot contact, formatting valid numbers according to your
                                    Phone Formatting Options.
                                </p>
                                <p class="mb-0">
                                    Enter the HubSpot contact id, e.g. <strong>99999999</strong>, or paste the full contact URL
                                    and the id will be extracted automatically, e.g.
                                    <strong>https://app.hubspot.com/contacts/999999/record/0-1/99999999</strong>.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showSingleContactHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined class="d-flex flex-column fill-height">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Validate Contacts</span>
                            <v-btn icon small @click="showValidateContactsHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Validate every contact, or just one segment.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text class="flex-grow-1"></v-card-text>
                        <v-card-actions class="px-4">
                            <v-btn color="primary" :disabled="!!activeRun" @click="guardedOpenValidateContactsDialog">
                                Validate Contacts
                            </v-btn>
                        </v-card-actions>
                    </v-card>

                    <v-dialog v-model="showValidateContactsHelp" max-width="500">
                        <v-card>
                            <v-card-title>Validate Contacts</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Validates all of your configured phone properties - Phone, Mobile Phone, and any custom
                                    properties - for every contact in your HubSpot portal, or just the contacts in one
                                    segment (list), formatting valid numbers according to your Phone Formatting Options.
                                </p>
                                <p class="mb-0">
                                    Before starting, you'll see an estimated validation count. Validations are drawn from your
                                    free monthly allowance first, then from your purchased balance. If automatic renewal is on,
                                    more are bought for you when you run out. Only one sync - full or segment - can run at a
                                    time for your account.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showValidateContactsHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-dialog v-model="showValidateContactsDialog" max-width="560" persistent>
                        <v-card>
                            <v-card-title class="d-flex align-center">
                                <v-icon color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
                                Validate Contacts
                                <v-spacer></v-spacer>
                                <v-btn icon small @click="closeValidateContactsDialog">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-card-subtitle>Validate every contact, or just one segment.</v-card-subtitle>
                            <v-divider></v-divider>
                            <v-card-text class="pt-5">
                                <div class="text-overline grey--text text--darken-1 mb-2" style="line-height: 1.2;">
                                    What do you want to validate?
                                </div>
                                <v-btn-toggle v-model="validateScope" mandatory color="primary" dense class="mb-4" style="width: 100%;">
                                    <v-btn value="all" style="flex: 1;">
                                        <v-icon left small>mdi-account-group-outline</v-icon>
                                        All Contacts
                                    </v-btn>
                                    <v-btn value="segment" style="flex: 1;">
                                        <v-icon left small>mdi-filter-variant</v-icon>
                                        A Segment
                                    </v-btn>
                                </v-btn-toggle>

                                <v-autocomplete
                                    v-if="validateScope === 'segment'"
                                    v-model="selectedSegmentId"
                                    :items="segments"
                                    :loading="loadingSegments"
                                    item-text="name"
                                    item-value="listId"
                                    label="HubSpot segment"
                                    prepend-inner-icon="mdi-format-list-bulleted"
                                    outlined
                                    dense
                                    clearable
                                    class="mb-2"
                                >
                                    <template v-slot:item="{ item }">
                                        {{ item.name }}
                                        <span v-if="item.memberCount !== null && item.memberCount !== undefined" class="grey--text ml-1">({{ item.memberCount.toLocaleString() }})</span>
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        {{ item.name }}
                                        <span v-if="item.memberCount !== null && item.memberCount !== undefined" class="grey--text ml-1">({{ item.memberCount.toLocaleString() }})</span>
                                    </template>
                                </v-autocomplete>

                                <v-btn
                                    v-if="!estimate"
                                    block
                                    color="primary"
                                    :disabled="validateScope === 'segment' && !selectedSegmentId"
                                    :loading="loadingEstimate"
                                    class="mt-2"
                                    @click="loadEstimate"
                                >
                                    <v-icon left small>mdi-calculator-variant</v-icon>
                                    Get Estimate
                                </v-btn>

                                <v-sheet v-if="estimate" outlined rounded class="pa-4 mt-2">
                                    <div class="d-flex align-baseline">
                                        <span class="text-h3 font-weight-bold">{{ estimate.estimatedCount.toLocaleString() }}</span>
                                        <span class="grey--text ml-2">estimated validations</span>
                                    </div>

                                    <v-divider class="my-4"></v-divider>
                                    <div class="d-flex align-center justify-space-between mb-1">
                                        <span class="grey--text text--darken-1">Available validations</span>
                                        <span class="font-weight-medium">{{ estimate.availableValidations.toLocaleString() }}</span>
                                    </div>
                                    <div class="caption grey--text text--darken-1 text-right">
                                        {{ estimate.freeRemaining.toLocaleString() }} free this month + {{ estimate.creditBalance.toLocaleString() }} purchased
                                    </div>

                                    <v-alert v-if="estimate.shortfallCount <= 0" type="success" dense outlined class="mb-0 mt-4">
                                        Your available validations cover this run.
                                    </v-alert>
                                    <v-alert v-else-if="estimate.autoRenewEnabled" type="info" dense outlined class="mb-0 mt-4">
                                        Your balance covers {{ estimate.availableValidations.toLocaleString() }};
                                        this run will automatically renew {{ estimate.renewalsNeeded }}
                                        {{ estimate.renewalsNeeded === 1 ? 'time' : 'times' }}
                                        (~{{ formatCents(estimate.renewalCostCents) }}).
                                    </v-alert>
                                    <v-alert v-else type="warning" dense outlined class="mb-0 mt-4">
                                        Only {{ estimate.availableValidations.toLocaleString() }} of ~{{ estimate.estimatedCount.toLocaleString() }}
                                        validations are covered; the run will stop when they run out.
                                        <a @click="goToBilling">Buy validations</a>
                                    </v-alert>
                                </v-sheet>

                                <v-alert v-if="startError" type="error" dense outlined class="mb-0 mt-4">
                                    {{ startError }}
                                    <a @click="goToBilling">Go to Billing</a>
                                </v-alert>
                            </v-card-text>
                            <v-divider v-if="estimate"></v-divider>
                            <v-card-actions class="px-4 py-3">
                                <v-btn text @click="closeValidateContactsDialog">Cancel</v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="estimate"
                                    color="primary"
                                    :loading="startingValidation"
                                    :disabled="estimate.shortfallCount > 0 && !estimate.autoRenewEnabled && estimate.availableValidations <= 0"
                                    @click="startValidation"
                                >
                                    <v-icon left small>mdi-play</v-icon>
                                    Start Sync
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" v-if="activeRun">
                    <v-card outlined>
                        <v-card-title>Active Sync Progress</v-card-title>
                        <v-card-subtitle>
                            Run: {{ activeRun.syncRunId }} | Status: {{ statusLabel(activeRun.status) }} | Scope: {{ activeRun.hubSpotListName || 'All Contacts' }}
                        </v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-progress-linear :value="activeRunProgress" color="primary" height="16" rounded>
                                <strong>{{ activeRunProgress }}%</strong>
                            </v-progress-linear>
                            <v-row class="mt-3">
                                <v-col cols="6" md="2">Scanned: <strong>{{ activeRun.contactsScanned }}</strong></v-col>
                                <v-col cols="6" md="2">With phone: <strong>{{ activeRun.contactsWithPhone }}</strong></v-col>
                                <v-col cols="6" md="2">Validated: <strong>{{ activeRun.contactsUpdated }}</strong></v-col>
                                <v-col cols="6" md="2">Failed: <strong>{{ activeRun.contactsFailed }}</strong></v-col>
                                <v-col cols="6" md="2">Batches done: <strong>{{ activeRun.bulkRequestsCompleted }}</strong></v-col>
                                <v-col cols="6" md="2">Batches in progress: <strong>{{ activeRun.bulkRequestsInProgress }}</strong></v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Last Sync Runs</span>
                            <v-btn small text color="primary" :loading="loadingDashboard" @click="loadSyncDashboard">Refresh</v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-data-table
                            :headers="historyHeaders"
                            :items="recentRuns"
                            :items-per-page="-1"
                            hide-default-footer
                            dense
                        >
                            <template v-slot:item.status="{ item }">
                                <v-tooltip v-if="item.errorMessage" bottom max-width="320">
                                    <template v-slot:activator="{ on }">
                                        <v-chip x-small :color="statusColor(item.status)" dark v-on="on">{{ statusLabel(item.status) }}</v-chip>
                                    </template>
                                    <span>{{ item.errorMessage }}</span>
                                </v-tooltip>
                                <v-chip v-else x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
                            </template>
                            <template v-slot:item.scope="{ item }">
                                {{ item.hubSpotListName || 'All Contacts' }}
                            </template>
                            <template v-slot:item.startedAt="{ item }">
                                {{ formatDate(item.startedAt) }}
                            </template>
                            <template v-slot:item.createdAt="{ item }">
                                {{ formatDate(item.createdAt) }}
                            </template>
                            <template v-slot:item.completedAt="{ item }">
                                {{ formatDate(item.completedAt) }}
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
            </v-tab-item>

            <v-tab-item>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="mb-4">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Phone Properties to Validate</span>
                            <v-btn icon small @click="showPhonePropertiesHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Choose which phone properties to validate.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-switch v-model="phoneProperties.validatePhone" label="Phone" hide-details class="mb-2"></v-switch>
                            <v-switch v-model="phoneProperties.validateMobilePhone" label="Mobile Phone" hide-details class="mb-4"></v-switch>

                            <div class="d-flex flex-wrap align-center">
                                <v-chip
                                    v-for="customProperty in phoneProperties.customProperties"
                                    :key="customProperty"
                                    class="mr-2 mb-2"
                                    close
                                    @click:close="removeCustomProperty(customProperty)"
                                >
                                    {{ propertyLabel(customProperty) }}
                                </v-chip>
                                <v-btn
                                    v-if="phoneProperties.customProperties.length < 3"
                                    icon
                                    small
                                    outlined
                                    class="mb-2"
                                    title="Add a custom property"
                                    @click="openPropertyPicker"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <v-alert v-if="!hasAnyPhoneProperty" type="warning" dense outlined class="mb-0 mt-4">
                                At least one property must be selected.
                            </v-alert>
                        </v-card-text>
                        <template v-if="phonePropertiesDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingPhoneProperties" @click="cancelPhonePropertiesOptions">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    small
                                    :loading="savingPhoneProperties"
                                    :disabled="!hasAnyPhoneProperty"
                                    @click="savePhonePropertiesOptions"
                                >
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showPhonePropertiesHelp" max-width="500">
                        <v-card>
                            <v-card-title>Phone Properties to Validate</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Choose which properties on a HubSpot contact Validate IQ checks and formats: the built-in
                                    <strong>Phone</strong> and <strong>Mobile Phone</strong> properties, plus up to 3 custom properties
                                    you pick from your HubSpot schema.
                                </p>
                                <p>
                                    Custom properties are validated when a contact is created (if Continuous Monitoring is on), when you use
                                    <strong>Validate Specific Contact</strong>, and during <strong>Validate All Contacts</strong> or
                                    <strong>Validate Segment</strong>. Continuous Monitoring does not revalidate a custom property when it's
                                    edited afterward - only Phone and Mobile Phone get validated live on every change.
                                </p>
                                <p class="mb-0">
                                    Adding a custom property creates a matching set of diagnostic properties named after it, e.g.
                                    a property labeled <strong>Another Phone</strong> gets its own
                                    <strong>Validate IQ Another Phone Status</strong>, <strong>Line Type</strong>,
                                    <strong>Carrier</strong>, and <strong>Last Validated At</strong> properties.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showPhonePropertiesHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-dialog v-model="showPropertyPicker" max-width="480">
                        <v-card>
                            <v-card-title>Add Custom Property</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <v-autocomplete
                                    v-model="selectedPropertyToAdd"
                                    :items="pickablePropertyOptions"
                                    :loading="loadingAvailableProperties"
                                    item-text="label"
                                    item-value="name"
                                    label="Contact property"
                                    dense
                                    clearable
                                ></v-autocomplete>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showPropertyPicker = false">Cancel</v-btn>
                                <v-btn color="primary" text :disabled="!selectedPropertyToAdd" @click="addCustomProperty">Add</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Phone Formatting Options</span>
                            <v-btn icon small @click="showFormattingExamples = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Format valid phone numbers automatically.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-switch v-model="phoneFormatting.formatNumbers" label="Format Numbers"></v-switch>
                            <template v-if="phoneFormatting.formatNumbers">
                                <v-switch v-model="phoneFormatting.addCountryCodeEnabled" label="Add country code (+1)"></v-switch>
                                <v-switch v-model="phoneFormatting.preserveExtension" label="Preserve extension"></v-switch>
                            </template>
                        </v-card-text>
                        <template v-if="phoneFormattingDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingPhoneFormatting" @click="cancelPhoneFormattingOptions">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" small :loading="savingPhoneFormatting" @click="savePhoneFormattingOptions">
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showFormattingExamples" max-width="500">
                        <v-card>
                            <v-card-title>Formatting Examples</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Format Numbers</div>
                                    <div>
                                        When off, the phone number in HubSpot is never updated.<br>When on, any number Bandwidth confirms
                                        as valid is always formatted the same combined way:
                                        <code>5559999999</code> &rarr; <code>(555) 999-9999</code>.<br>Invalid numbers are left unchanged.
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Add country code (+1)</div>
                                    <div><code>5559999999</code> &rarr; <code>+1 (555) 999-9999</code></div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Preserve extension</div>
                                    <div class="mb-1"><code>5559999999 x123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                    <div class="mb-1"><code>5559999999 ext 123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                    <div><code>5559999999 123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                </div>
                                <v-divider class="mb-3"></v-divider>
                                <div>
                                    <div class="grey--text text--darken-1">All three together</div>
                                    <div><code>5559999999 x123</code> &rarr; <code>+1 (555) 999-9999 ext123</code></div>
                                </div>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showFormattingExamples = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <div class="d-flex align-center">
                                <span>Continuous Monitoring</span>
                                <v-tooltip bottom :disabled="liveSyncAllowed" max-width="300">
                                    <template v-slot:activator="{ on }">
                                        <span v-on="on">
                                            <v-switch
                                                v-model="liveSyncEnabled"
                                                inset
                                                hide-details
                                                class="ma-0 pa-0 ml-3"
                                                :disabled="savingLiveSync || (!liveSyncAllowed && !liveSyncEnabled)"
                                            ></v-switch>
                                        </span>
                                    </template>
                                    <span>Live Sync requires automatic renewal and available validations.</span>
                                </v-tooltip>
                            </div>
                            <v-btn icon small @click="showLiveSyncHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Automatically validate phone numbers when contacts are created or when their phone or mobile phone numbers are updated.</v-card-subtitle>
                        <v-card-text v-if="!liveSyncAllowed" class="pt-0 pb-3">
                            <span class="caption grey--text text--darken-1">
                                Live Sync requires automatic renewal and available validations.
                                <a @click="goToBilling">Go to Billing</a>
                            </span>
                        </v-card-text>
                        <v-card-text v-if="usage.liveSyncSuspendedForBalance" class="pt-0 pb-3">
                            <v-alert type="info" dense outlined class="mb-0">
                                Live Sync was paused because your validations ran out. It will resume automatically after payment succeeds.
                            </v-alert>
                        </v-card-text>
                        <template v-if="liveSyncDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingLiveSync" @click="cancelLiveSync">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" small :loading="savingLiveSync" @click="saveLiveSync">
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showLiveSyncHelp" max-width="500">
                        <v-card>
                            <v-card-title>Continuous Monitoring</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Continuous Monitoring listens for HubSpot events and validates a contact's phone in near real time,
                                    without waiting for a manual validation or a full sync:
                                </p>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Contact created</div>
                                    <div>Every configured phone property already populated on the new contact - Phone, Mobile Phone, and any custom properties - is validated.</div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Phone or Mobile Phone updated</div>
                                    <div>Only the property that changed is revalidated immediately.</div>
                                </div>
                                <div>
                                    <div class="grey--text text--darken-1">Custom properties</div>
                                    <div>Only validated when a contact is first created - editing a custom property afterward does not trigger Continuous Monitoring. Use <strong>Validate Specific Contact</strong>, <strong>Validate All Contacts</strong>, or <strong>Validate Segment</strong> to revalidate it.</div>
                                </div>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showLiveSyncHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>
            </v-row>
            </v-tab-item>

            <v-tab-item>
            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Your validations</span>
                            <v-progress-circular v-if="loadingUsage" indeterminate size="18" width="2" color="primary"></v-progress-circular>
                        </v-card-title>
                        <v-card-subtitle>Purchased validations never expire. They are used after your free monthly validations.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <div class="text-overline grey--text text--darken-1" style="line-height: 1.2;">Purchased Validations Available</div>
                                    <div class="d-flex align-baseline flex-wrap">
                                        <span class="text-h3 font-weight-bold">{{ formatNumber(usage.creditBalance) }}</span>
                                        <span v-if="usage.creditBalanceBaseline > 0" class="grey--text ml-2">
                                            of {{ formatNumber(usage.creditBalanceBaseline) }} in your latest purchase
                                        </span>
                                    </div>
                                    <template v-if="usage.creditBalanceBaseline > 0">
                                        <v-progress-linear
                                            :value="creditUsedPercent"
                                            :color="creditBarColor"
                                            background-color="grey lighten-3"
                                            height="12"
                                            rounded
                                            class="mt-3"
                                        ></v-progress-linear>
                                        <div class="d-flex justify-space-between caption grey--text text--darken-1 mt-1">
                                            <span><strong>{{ formatNumber(creditUsedInBatch) }}</strong> used ({{ creditUsedPercent }}%)</span>
                                            <span><strong>{{ formatNumber(usage.creditBalance) }}</strong> left</span>
                                        </div>
                                    </template>
                                    <div v-else class="caption grey--text text--darken-1 mt-2">
                                        You have not purchased validations yet. Pick a pack below to get started.
                                    </div>
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-row dense>
                                        <v-col cols="6">
                                            <div class="text-overline grey--text text--darken-1" style="line-height: 1.2;">Total purchased</div>
                                            <div class="text-h5 font-weight-medium">{{ formatNumber(usage.totalCreditsPurchased) }}</div>
                                        </v-col>
                                        <v-col cols="6">
                                            <div class="text-overline grey--text text--darken-1" style="line-height: 1.2;">Total used</div>
                                            <div class="text-h5 font-weight-medium">{{ formatNumber(usage.totalCreditsUsed) }}</div>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="my-3"></v-divider>
                                    <div class="d-flex align-center justify-space-between">
                                        <span class="grey--text text--darken-1">
                                            <strong>{{ formatNumber(usage.freeRemaining) }}</strong> of {{ formatNumber(usage.monthlyFreeLimit) }} free validations left this month
                                        </span>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title>Automatic renewal</v-card-title>
                        <v-card-subtitle>
                            When you run out of validations, automatically buy the same pack again.
                            Required for Live Sync (continuous monitoring).
                        </v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-alert v-if="usage.renewalFailed" type="error" prominent outlined class="mb-4">
                                <div class="font-weight-bold">Automatic renewal failed</div>
                                <div>{{ usage.renewalFailureReason || 'We could not process the renewal payment.' }}</div>
                                <div class="mt-3 d-flex flex-wrap">
                                    <v-btn
                                        v-if="usage.pendingRenewalInvoiceUrl"
                                        color="error"
                                        small
                                        class="mr-2 mb-1"
                                        :href="usage.pendingRenewalInvoiceUrl"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        Pay invoice
                                    </v-btn>
                                    <v-btn v-if="usage.paymentConfigured" color="error" small outlined class="mb-1" :loading="openingPortal" @click="managePaymentMethod">
                                        Manage payment method
                                    </v-btn>
                                </div>
                            </v-alert>

                            <v-alert v-if="usage.billingExempt" type="info" dense text class="mb-4">
                                Test account: purchases and automatic renewals add validations at no charge and don't use Stripe.
                            </v-alert>

                            <v-alert v-if="usage.liveSyncSuspendedForBalance" type="info" dense outlined class="mb-4">
                                Live Sync was paused because your validations ran out. It will resume automatically after payment succeeds.
                            </v-alert>

                            <div class="d-flex flex-wrap align-center">
                                <v-switch
                                    :key="autoRenewSwitchKey"
                                    :input-value="usage.autoRenewEnabled"
                                    :disabled="togglingAutoRenew"
                                    :loading="togglingAutoRenew"
                                    inset
                                    hide-details
                                    class="ma-0 pa-0 mr-3"
                                    label="Automatically renew when I run out"
                                    @change="onAutoRenewSwitch"
                                ></v-switch>
                                <v-chip v-if="autoRenewDiscountPercent" small color="success" text-color="white" class="mr-3">
                                    Save {{ autoRenewDiscountPercent }}%
                                </v-chip>
                                <v-btn v-if="usage.paymentConfigured" small text color="primary" :loading="openingPortal" @click="managePaymentMethod">
                                    Manage payment method
                                </v-btn>
                            </div>

                            <div class="mt-3">
                                <template v-if="usage.autoRenewEnabled && usage.renewalValidations">
                                    Renews with <strong>{{ formatNumber(usage.renewalValidations) }} validations</strong>
                                    for <strong>{{ formatCents(renewalDisplayPriceCents) }}</strong><span v-if="autoRenewDiscountPercent"> ({{ autoRenewDiscountPercent }}% automatic renewal discount)</span>.
                                </template>
                                <template v-else-if="usage.autoRenewEnabled">
                                    Automatic renewal is on.
                                </template>
                                <template v-else>
                                    <span class="grey--text text--darken-1">
                                        Pay as you go: you will need to buy more validations yourself when you run out.
                                        Turning this on takes you to Stripe to buy the renewal pack and keep your card on file for renewals.
                                        <template v-if="autoRenewDiscountPercent">Automatic renewal packs are {{ autoRenewDiscountPercent }}% off.</template>
                                    </span>
                                </template>
                            </div>

                            <v-select
                                v-if="plans.tiers.length"
                                v-model="renewalTierSelection"
                                :items="renewalTierItems"
                                item-text="label"
                                item-value="validations"
                                label="Renewal pack"
                                outlined
                                dense
                                hide-details
                                class="mt-4"
                                style="max-width: 360px;"
                                :disabled="togglingAutoRenew"
                                @change="onRenewalTierChange"
                            ></v-select>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex align-center">
                            <span>Buy validations</span>
                            <v-spacer></v-spacer>
                            <v-menu offset-y left :close-on-content-click="false" max-width="420" min-width="320">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-bind="attrs" v-on="on" aria-label="How pack prices are calculated">
                                        <v-icon>mdi-information-outline</v-icon>
                                    </v-btn>
                                </template>
                                <v-card>
                                    <v-card-title class="subtitle-1 font-weight-bold">How pack prices are calculated</v-card-title>
                                    <v-card-text class="pb-2">
                                        <div class="body-2 grey--text text--darken-1 mb-3">
                                            Each validation in a pack is priced at the rate of the band it falls in, so larger packs cost less per validation.
                                            The first {{ formatNumber(plans.monthlyFreeLimit) }} validations every month are free and are not part of pack pricing.
                                            <template v-if="autoRenewDiscountPercent">
                                                Packs bought with automatic renewal on, and every automatic renewal, get {{ autoRenewDiscountPercent }}% off these prices.
                                            </template>
                                        </div>
                                        <v-simple-table dense>
                                            <template v-slot:default>
                                                <thead>
                                                    <tr>
                                                        <th class="text-left">Validations in the pack</th>
                                                        <th class="text-right">Price each</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in rateTierRows" :key="row.label">
                                                        <td>{{ row.label }}</td>
                                                        <td class="text-right font-weight-medium">{{ row.price }}</td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                        </v-card-title>
                        <v-card-subtitle>Choose a pack. The larger the pack, the lower the price per validation.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="8">
                                    <div v-if="loadingPlans && !plans.tiers.length" class="text-center py-6">
                                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                    </div>
                                    <v-alert v-else-if="!plans.tiers.length" type="warning" dense outlined class="mb-0">
                                        Pricing could not be loaded.
                                        <a @click="loadPlans">Try again</a>
                                    </v-alert>
                                    <v-row v-else dense>
                                        <v-col v-for="tier in plans.tiers" :key="tier.validations" cols="6" sm="4">
                                            <v-card
                                                outlined
                                                class="tier-card text-center fill-height"
                                                :class="{ 'tier-card--selected': tier.validations === selectedTier }"
                                                role="button"
                                                tabindex="0"
                                                @click="selectTier(tier.validations)"
                                                @keydown.enter.prevent="selectTier(tier.validations)"
                                                @keydown.space.prevent="selectTier(tier.validations)"
                                            >
                                                <v-card-text class="py-4">
                                                    <div class="text-h5 font-weight-bold">{{ formatNumber(tier.validations) }}</div>
                                                    <div class="caption grey--text text--darken-1">validations</div>
                                                    <div v-if="purchaseDiscountActive" class="caption grey--text text--darken-1 mt-2" style="text-decoration: line-through;">{{ formatCents(tier.priceCents) }}</div>
                                                    <div class="text-h6 font-weight-bold primary--text" :class="purchaseDiscountActive ? '' : 'mt-2'">{{ formatCents(tierPriceCents(tier)) }}</div>
                                                    <div class="caption grey--text text--darken-1">{{ formatPerValidation(tier, tierPriceCents(tier)) }} / validation</div>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>
                                    </v-row>

                                    <template v-if="plans.tiers.length">
                                        <div class="d-flex flex-wrap align-center mt-4">
                                            <v-checkbox
                                                :input-value="purchaseAutoRenewEffective"
                                                :disabled="!!usage.autoRenewEnabled"
                                                label="Automatically renew when I run out"
                                                hide-details
                                                class="ma-0 pa-0 mr-3"
                                                @change="onPurchaseAutoRenewChange"
                                            ></v-checkbox>
                                            <v-chip v-if="autoRenewDiscountPercent" small color="success" text-color="white">
                                                Save {{ autoRenewDiscountPercent }}%
                                            </v-chip>
                                        </div>
                                        <div v-if="usage.autoRenewEnabled" class="caption grey--text text--darken-1 ml-8">
                                            Automatic renewal is already on, so this purchase gets the discount. Turn it off with the switch above.
                                        </div>
                                        <div v-else class="caption grey--text text--darken-1 ml-8">
                                            Stripe keeps your card on file and buys this pack again when your validations run out. Required for Live Sync.
                                        </div>
                                        <v-btn
                                            color="primary"
                                            large
                                            class="mt-4"
                                            :disabled="!selectedTierObject"
                                            :loading="purchasing"
                                            @click="requestPurchase"
                                        >
                                            <v-icon left>mdi-cart-outline</v-icon>
                                            <template v-if="selectedTierObject">
                                                Purchase {{ formatNumber(selectedTierObject.validations) }} validations - {{ formatCents(tierPriceCents(selectedTierObject)) }}
                                            </template>
                                            <template v-else>Purchase</template>
                                        </v-btn>
                                        <div class="caption grey--text text--darken-1 mt-2">You will be taken to Stripe to pay securely.</div>
                                    </template>
                                </v-col>

                                <v-col cols="12" md="4">
                                    <v-sheet outlined rounded class="pa-4 fill-height">
                                        <div class="font-weight-bold mb-2">Everything included</div>
                                        <div class="caption grey--text text--darken-1 mb-2">
                                            Every pack includes the same features, plus {{ formatNumber(plans.monthlyFreeLimit) }} free validations each month.
                                        </div>
                                        <v-list dense class="py-0 transparent">
                                            <v-list-item v-for="feature in includedFeatures" :key="feature.text" class="px-0">
                                                <v-list-item-icon class="mr-2 my-2">
                                                    <v-icon v-if="featureUnavailable(feature)" small color="error">mdi-close-circle-outline</v-icon>
                                                    <v-icon v-else small color="success">mdi-check-circle-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content class="py-1">
                                                    <div class="text-body-2">{{ feature.text }}</div>
                                                    <div v-if="feature.note" class="caption grey--text text--darken-1">{{ feature.note }}</div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Invoices</span>
                            <v-btn small text color="primary" :loading="loadingInvoices" @click="loadInvoices">Refresh</v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-data-table
                            :headers="invoiceHeaders"
                            :items="invoices"
                            :options.sync="invoicesOptions"
                            :server-items-length="invoicesTotal"
                            :loading="loadingInvoices"
                            :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
                            dense
                        >
                            <template v-slot:item.type="{ item }">
                                {{ invoiceTypeLabel(item.type) }}
                            </template>
                            <template v-slot:item.amountCents="{ item }">
                                {{ formatCents(item.amountCents) }}
                            </template>
                            <template v-slot:item.issuedAt="{ item }">
                                {{ formatDate(item.issuedAt) }}
                            </template>
                            <template v-slot:item.hostedInvoiceUrl="{ item }">
                                <a v-if="item.hostedInvoiceUrl" :href="item.hostedInvoiceUrl" target="_blank" rel="noopener">View</a>
                                <span v-else class="grey--text">-</span>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
            </v-tab-item>
            </v-tabs-items>
        </template>
    </v-container>
</template>

<script>
export default {
    data() {
        return {
            loading: true,
            connectingToHubSpot: false,
            hubspotDefaultPropertyGroups: [
                {
                    label: 'Phone',
                    properties: [
                        { label: 'Validate IQ Phone Status', description: 'Last validation result for Phone - a Valid / Invalid / Unknown dropdown.' },
                        { label: 'Validate IQ Phone Line Type', description: "The Phone number's line type returned by Bandwidth (e.g. mobile, landline, voip)." },
                        { label: 'Validate IQ Phone Carrier', description: "The Phone number's carrier returned by Bandwidth." },
                        { label: 'Validate IQ Phone Last Validated At', description: 'When Phone was last validated.' }
                    ]
                },
                {
                    label: 'Mobile Phone',
                    properties: [
                        { label: 'Validate IQ Mobile Phone Status', description: 'Last validation result for Mobile Phone - a Valid / Invalid / Unknown dropdown.' },
                        { label: 'Validate IQ Mobile Phone Line Type', description: "The Mobile Phone number's line type returned by Bandwidth." },
                        { label: 'Validate IQ Mobile Phone Carrier', description: "The Mobile Phone number's carrier returned by Bandwidth." },
                        { label: 'Validate IQ Mobile Phone Last Validated At', description: 'When Mobile Phone was last validated.' }
                    ]
                }
            ],
            syncingProperties: false,
            validatingSingleContact: false,
            loadingDashboard: false,
            segments: [],
            loadingSegments: false,
            selectedSegmentId: null,
            validateScope: 'all',
            showValidateContactsHelp: false,
            showValidateContactsDialog: false,
            estimate: null,
            loadingEstimate: false,
            startingValidation: false,
            showFormattingExamples: false,
            savingPhoneFormatting: false,
            savedPhoneFormatting: null,
            phoneProperties: {
                validatePhone: true,
                validateMobilePhone: true,
                customProperties: []
            },
            savedPhoneProperties: null,
            savingPhoneProperties: false,
            showPropertyPicker: false,
            showPhonePropertiesHelp: false,
            loadingAvailableProperties: false,
            availableProperties: [],
            selectedPropertyToAdd: null,
            liveSyncEnabled: false,
            savedLiveSyncEnabled: null,
            savingLiveSync: false,
            showLiveSyncHelp: false,
            status: {
                hubspotConnected: false,
                propertiesCreated: false
            },
            singleContactId: '',
            singleContactResult: null,
            showSingleContactHelp: false,
            dashboard: {
                activeRun: null,
                recentRuns: []
            },
            phoneFormatting: {
                formatNumbers: false,
                addCountryCodeEnabled: false,
                preserveExtension: false
            },
            poller: null,
            showUnsavedChangesConfirm: false,
            pendingConfirmedAction: null,
            activeTab: 0,
            settingsTabIndex: 1, // matches the "Settings" v-tab's position
            billingTabIndex: 2, // matches the "Billing" v-tab's position (Validate, Settings, Billing)
            loadingUsage: false,
            usage: {
                paymentConfigured: false,
                monthlyFreeLimit: 100,
                freeRemaining: 0,
                creditBalance: 0,
                creditBalanceBaseline: 0,
                totalCreditsPurchased: 0,
                totalCreditsUsed: 0,
                availableValidations: 0,
                autoRenewEnabled: false,
                billingExempt: false,
                renewalValidations: null,
                renewalPriceCents: null,
                liveSyncSuspendedForBalance: false,
                renewalFailed: false,
                renewalFailureReason: null,
                pendingRenewalInvoiceUrl: null,
                billingEmail: null
            },
            plans: {
                monthlyFreeLimit: 100,
                autoRenewDiscountPercent: 0,
                tiers: [],
                rateTiers: []
            },
            loadingPlans: false,
            selectedTier: null,
            purchaseAutoRenew: false,
            purchaseAutoRenewTouched: false,
            purchasing: false,
            renewalTierSelection: null,
            togglingAutoRenew: false,
            autoRenewSwitchKey: 0,
            openingPortal: false,
            autoRenewConfirm: {
                show: false,
                action: 'autoRenew',
                title: '',
                subtitle: '',
                icon: 'mdi-help-circle-outline',
                iconBg: 'primary',
                rows: [],
                notes: [],
                confirmLabel: 'Continue',
                enabled: false,
                validations: null
            },
            snackbar: {
                show: false,
                text: '',
                color: 'success'
            },
            pendingBillingReturn: null,
            billingPollToken: 0,
            startError: null,
            includedFeatures: [
                { text: 'Discover line type' },
                { text: 'Identify carrier' },
                { text: 'Current line status' },
                { text: 'Error detection' },
                { text: 'Validate numbers in custom properties' },
                { text: 'Optionally format numbers' },
                { text: 'Continuous monitoring (Live Sync)', note: 'Requires automatic renewal', requiresAutoRenew: true }
            ],
            loadingInvoices: false,
            invoices: [],
            invoicesTotal: 0,
            invoicesOptions: { page: 1, itemsPerPage: 10 }
        }
    },
    computed: {
        phoneFormattingDirty() {
            if (!this.savedPhoneFormatting) {
                return false;
            }
            return JSON.stringify(this.phoneFormatting) !== JSON.stringify(this.savedPhoneFormatting);
        },
        phonePropertiesDirty() {
            if (!this.savedPhoneProperties) {
                return false;
            }
            return JSON.stringify(this.phoneProperties) !== JSON.stringify(this.savedPhoneProperties);
        },
        hasAnyPhoneProperty() {
            return this.phoneProperties.validatePhone
                || this.phoneProperties.validateMobilePhone
                || this.phoneProperties.customProperties.length > 0;
        },
        liveSyncDirty() {
            if (this.savedLiveSyncEnabled === null) {
                return false;
            }
            return this.liveSyncEnabled !== this.savedLiveSyncEnabled;
        },
        pickablePropertyOptions() {
            const selected = new Set(this.phoneProperties.customProperties.map(p => (p || '').toLowerCase()));
            return this.availableProperties.filter(p => !selected.has((p.name || '').toLowerCase()));
        },
        hasUnsavedChanges() {
            return this.phoneFormattingDirty || this.phonePropertiesDirty || this.liveSyncDirty;
        },
        liveSyncAllowed() {
            return !!this.usage.autoRenewEnabled && this.usage.availableValidations > 0;
        },
        activeRun() {
            return this.dashboard.activeRun;
        },
        recentRuns() {
            return this.dashboard.recentRuns || [];
        },
        rateTierRows() {
            return (this.plans.rateTiers || []).map((tier, index) => {
                let label;
                if (tier.to === null || tier.to === undefined) {
                    label = `${this.formatNumber(tier.from)} and above`;
                } else if (index === 0) {
                    label = `First ${this.formatNumber(tier.to)}`;
                } else {
                    label = `${this.formatNumber(tier.from)} - ${this.formatNumber(tier.to)}`;
                }

                return { label, price: `$${Number(tier.pricePerValidation).toFixed(3)}` };
            });
        },
        invoiceHeaders() {
            return [
                { text: 'Date', value: 'issuedAt' },
                { text: 'Type', value: 'type' },
                { text: 'Amount', value: 'amountCents' },
                { text: 'Status', value: 'status' },
                { text: 'Invoice', value: 'hostedInvoiceUrl' }
            ];
        },
        activeRunProgress() {
            if (!this.activeRun) {
                return 0;
            }

            const total = this.activeRun.contactsWithPhone || 0;
            if (total <= 0) {
                return 0;
            }

            const done = (this.activeRun.contactsUpdated || 0) + (this.activeRun.contactsFailed || 0);
            return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
        },
        historyHeaders() {
            return [
                { text: 'Run ID', value: 'syncRunId' },
                { text: 'Scope', value: 'scope' },
                { text: 'Status', value: 'status' },
                { text: 'Started', value: 'startedAt' },
                { text: 'Completed', value: 'completedAt' },
                { text: 'Created', value: 'createdAt' },
                { text: 'Validated', value: 'contactsUpdated' },
                { text: 'Failed', value: 'contactsFailed' }
            ];
        },
        creditUsedInBatch() {
            const baseline = this.usage.creditBalanceBaseline || 0;
            return Math.max(0, baseline - (this.usage.creditBalance || 0));
        },
        creditUsedPercent() {
            const baseline = this.usage.creditBalanceBaseline || 0;
            if (baseline <= 0) {
                return 0;
            }

            return Math.max(0, Math.min(100, Math.round((this.creditUsedInBatch / baseline) * 100)));
        },
        creditBarColor() {
            if (this.usage.creditBalance <= 0) {
                return 'error';
            }

            return this.creditUsedPercent >= 80 ? 'warning' : 'primary';
        },
        selectedTierObject() {
            return this.plans.tiers.find(tier => tier.validations === this.selectedTier) || null;
        },
        autoRenewDiscountPercent() {
            return this.plans.autoRenewDiscountPercent || 0;
        },
        // Automatic renewal prices apply to a purchase that turns auto-renew on, or made while it is already on.
        purchaseAutoRenewEffective() {
            return !!this.purchaseAutoRenew || !!this.usage.autoRenewEnabled;
        },
        purchaseDiscountActive() {
            return this.autoRenewDiscountPercent > 0 && this.purchaseAutoRenewEffective;
        },
        renewalTierItems() {
            const items = this.plans.tiers.map(tier => ({
                validations: tier.validations,
                label: `${this.formatNumber(tier.validations)} validations - ${this.formatCents(tier.autoRenewPriceCents)}`
            }));

            const current = this.usage.renewalValidations;
            if (current && !items.some(item => item.validations === current)) {
                const price = this.usage.renewalPriceCents;
                items.push({
                    validations: current,
                    label: price === null || price === undefined
                        ? `${this.formatNumber(current)} validations`
                        : `${this.formatNumber(current)} validations - ${this.formatCents(price)}`
                });
            }

            return items;
        },
        renewalDisplayPriceCents() {
            if (this.usage.renewalPriceCents !== null && this.usage.renewalPriceCents !== undefined) {
                return this.usage.renewalPriceCents;
            }

            const tier = this.plans.tiers.find(t => t.validations === this.usage.renewalValidations);
            return tier ? tier.autoRenewPriceCents : 0;
        }
    },
    async created() {
        await this.$auth.auth0ClientPromise;
        await this.initializePage();
    },
    destroyed() {
        this.billingPollToken++;
        this.stopPolling();
    },
    watch: {
        invoicesOptions(next, previous) {
            if (next.page !== previous.page || next.itemsPerPage !== previous.itemsPerPage) {
                this.loadInvoices();
            }
        },
        activeTab(newTab) {
            if (newTab === this.billingTabIndex) {
                this.loadUsage();
                this.loadInvoices();
                if (!this.plans.tiers.length) {
                    this.loadPlans();
                }
            } else if (newTab === this.settingsTabIndex) {
                // Live Sync availability depends on auto-renew/balance, and the backend can
                // switch Live Sync on/off by itself, so refresh both when opening Settings.
                this.loadUsage();
                if (!this.liveSyncDirty && !this.savingLiveSync) {
                    this.loadLiveSync();
                }
            }
        },
        validateScope() {
            this.estimate = null;
            this.startError = null;
        },
        selectedSegmentId() {
            this.estimate = null;
            this.startError = null;
        }
    },
    methods: {
        async initializePage() {
            this.loading = true;
            this.handleOAuthReturnMessage();
            this.captureBillingReturn();
            await this.loadStatus();

            if (this.status.hubspotConnected) {
                await this.loadSyncDashboard();
                await this.loadSegments();
            }

            if (this.status.hubspotConnected && this.status.propertiesCreated) {
                await this.loadLiveSync();
                await this.loadPhoneProperties();
                await this.loadPhoneFormatting();
                await this.loadUsage();
                this.loadPlans();
            }

            this.loading = false;

            if (this.status.hubspotConnected && this.status.propertiesCreated) {
                // Not awaited: polling for the Stripe webhook can take several seconds.
                this.handleBillingReturn();
            }
        },
        // Reads (and strips) the ?billing=... param Stripe Checkout returns to.
        captureBillingReturn() {
            const billing = this.$route.query.billing;

            if (!billing) {
                return;
            }

            this.pendingBillingReturn = billing;

            const updatedQuery = Object.assign({}, this.$route.query);
            delete updatedQuery.billing;
            this.$router.replace({ query: updatedQuery }).catch(() => {});
        },
        billingBaselineKey() {
            return 'validateiq_billing_baseline';
        },
        // Called right before redirecting to Stripe so that, on return, we can tell whether
        // the webhook has already been applied (balance / auto-renew changed).
        rememberBillingBaseline() {
            try {
                window.sessionStorage.setItem(this.billingBaselineKey(), JSON.stringify(this.billingSnapshot()));
            } catch (e) {
                // sessionStorage unavailable - polling falls back to the first loaded usage.
            }
        },
        readBillingBaseline() {
            try {
                const raw = window.sessionStorage.getItem(this.billingBaselineKey());
                window.sessionStorage.removeItem(this.billingBaselineKey());
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        },
        billingSnapshot() {
            return {
                creditBalance: this.usage.creditBalance,
                autoRenewEnabled: this.usage.autoRenewEnabled
            };
        },
        billingChangedSince(baseline) {
            const now = this.billingSnapshot();
            return now.creditBalance !== baseline.creditBalance
                || now.autoRenewEnabled !== baseline.autoRenewEnabled;
        },
        showSnackbar(text, color) {
            this.snackbar = { show: true, text, color: color || 'success' };
        },
        async handleBillingReturn() {
            const result = this.pendingBillingReturn;
            this.pendingBillingReturn = null;

            if (!result) {
                return;
            }

            this.activeTab = this.billingTabIndex;
            const storedBaseline = this.readBillingBaseline();

            if (result === 'purchase-cancelled') {
                this.showSnackbar('Purchase cancelled.', 'info');
                return;
            }

            if (result === 'card-success') {
                // Back from the Stripe customer portal: nothing to wait for, just refresh.
                this.showSnackbar('Payment method settings refreshed.', 'success');
                await this.loadUsage();
                return;
            }

            if (result !== 'purchase-success') {
                return;
            }

            this.showSnackbar('Payment received. Updating your balance...', 'success');
            this.reloadInvoicesFromFirstPage();

            // Stripe's webhook can lag a couple of seconds behind the redirect.
            const baseline = storedBaseline || this.billingSnapshot();
            const token = ++this.billingPollToken;
            let changed = this.billingChangedSince(baseline);

            for (let attempt = 0; attempt < 6 && !changed; attempt++) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                if (token !== this.billingPollToken) {
                    return;
                }

                await this.loadUsage();
                changed = this.billingChangedSince(baseline);
            }

            if (token !== this.billingPollToken) {
                return;
            }

            if (changed) {
                this.reloadInvoicesFromFirstPage();
                this.showSnackbar('Your validations have been added.', 'success');
            } else {
                this.showSnackbar('Your payment is still being processed. Your balance will update shortly - refresh this page in a moment.', 'info');
            }
        },
        handleOAuthReturnMessage() {
            const result = this.$route.query.validateiq_result;
            const message = this.$route.query.message;

            if (!result) {
                return;
            }

            const toastType = result === 'success' || result === 'partial' ? 'success' : 'error';
            const title = result === 'success'
                ? 'HubSpot Connected'
                : (result === 'partial' ? 'Connected with Warnings' : 'HubSpot Connection Failed');

            this.$swal({
                icon: toastType,
                title: title,
                text: message || null,
                toast: true,
                timer: 4500,
                position: 'top-end'
            });

            const updatedQuery = Object.assign({}, this.$route.query);
            delete updatedQuery.validateiq_result;
            delete updatedQuery.message;
            this.$router.replace({ query: updatedQuery }).catch(() => {});
        },
        async loadStatus() {
            const response = await this.$store.dispatch('validateIq/getStatus');

            if (response && response.status === 200 && response.data) {
                this.status = {
                    hubspotConnected: !!response.data.hubspotConnected,
                    propertiesCreated: !!response.data.propertiesCreated
                };
                return;
            }

            this.status = {
                hubspotConnected: false,
                propertiesCreated: false
            };
            this.$swal({
                icon: 'error',
                title: 'Status Error',
                text: 'Could not load Validate IQ status. Please try again later.'
            });
        },
        async connectHubSpot() {
            this.connectingToHubSpot = true;
            const returnUrl = `${window.location.origin}${this.$route.path}`;
            const response = await this.$store.dispatch('validateIq/getConnectUrl', returnUrl);
            this.connectingToHubSpot = false;

            if (response && response.status === 200 && response.data && response.data.authorizationUrl) {
                window.location.href = response.data.authorizationUrl;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Connection Error',
                text: 'Could not create HubSpot authorization URL.'
            });
        },
        async syncProperties() {
            this.syncingProperties = true;
            const response = await this.$store.dispatch('validateIq/syncProperties');
            this.syncingProperties = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.$swal({
                    icon: 'success',
                    title: 'Properties Synced',
                    text: 'Validate IQ contact properties are now ready.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });
                await this.loadStatus();
                await this.loadSyncDashboard();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Sync Error',
                text: response && response.data && response.data.error ? response.data.error : 'Could not sync properties.'
            });
        },
        async loadLiveSync() {
            const response = await this.$store.dispatch('validateIq/getLiveSync');

            if (response && response.status === 200 && response.data) {
                this.liveSyncEnabled = !!response.data.enabled;
            }

            this.savedLiveSyncEnabled = this.liveSyncEnabled;
        },
        async loadUsage() {
            this.loadingUsage = true;
            const response = await this.$store.dispatch('validateIq/getUsage');
            this.loadingUsage = false;

            if (response && response.status === 200 && response.data) {
                const data = response.data;
                const toInt = value => (value === null || value === undefined ? 0 : value);
                const toNullable = value => (value === null || value === undefined ? null : value);

                this.usage = {
                    paymentConfigured: !!data.paymentConfigured,
                    monthlyFreeLimit: toInt(data.monthlyFreeLimit),
                    freeRemaining: toInt(data.freeRemaining),
                    creditBalance: toInt(data.creditBalance),
                    creditBalanceBaseline: toInt(data.creditBalanceBaseline),
                    totalCreditsPurchased: toInt(data.totalCreditsPurchased),
                    totalCreditsUsed: toInt(data.totalCreditsUsed),
                    availableValidations: toInt(data.availableValidations),
                    autoRenewEnabled: !!data.autoRenewEnabled,
                    billingExempt: !!data.billingExempt,
                    renewalValidations: toNullable(data.renewalValidations),
                    renewalPriceCents: toNullable(data.renewalPriceCents),
                    liveSyncSuspendedForBalance: !!data.liveSyncSuspendedForBalance,
                    renewalFailed: !!data.renewalFailed,
                    renewalFailureReason: toNullable(data.renewalFailureReason),
                    pendingRenewalInvoiceUrl: toNullable(data.pendingRenewalInvoiceUrl),
                    billingEmail: toNullable(data.billingEmail)
                };
                this.syncBillingSelections();
            }
        },
        async loadPlans() {
            this.loadingPlans = true;
            const response = await this.$store.dispatch('validateIq/getBillingPlans');
            this.loadingPlans = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.plans = {
                    monthlyFreeLimit: response.data.monthlyFreeLimit === null || response.data.monthlyFreeLimit === undefined
                        ? this.usage.monthlyFreeLimit
                        : response.data.monthlyFreeLimit,
                    autoRenewDiscountPercent: response.data.autoRenewDiscountPercent || 0,
                    tiers: Array.isArray(response.data.tiers) ? response.data.tiers : [],
                    rateTiers: Array.isArray(response.data.rateTiers) ? response.data.rateTiers : []
                };
                this.syncBillingSelections();
                return;
            }

            this.plans = { monthlyFreeLimit: this.usage.monthlyFreeLimit, autoRenewDiscountPercent: 0, tiers: [], rateTiers: [] };
        },
        // Keeps the tier pickers pointed at something sensible whenever usage or plans change.
        syncBillingSelections() {
            const validTier = value => this.plans.tiers.some(tier => tier.validations === value);
            const preferred = this.usage.renewalValidations;

            if (!validTier(this.selectedTier)) {
                this.selectedTier = validTier(preferred)
                    ? preferred
                    : (this.plans.tiers.length ? this.plans.tiers[0].validations : null);
            }

            if (!this.purchaseAutoRenewTouched) {
                this.purchaseAutoRenew = !!this.usage.autoRenewEnabled;
            }

            if (this.usage.renewalValidations) {
                this.renewalTierSelection = this.usage.renewalValidations;
            } else if (!this.renewalTierSelection && this.plans.tiers.length) {
                this.renewalTierSelection = this.plans.tiers[0].validations;
            }
        },
        selectTier(validations) {
            this.selectedTier = validations;
        },
        goToBilling() {
            this.showValidateContactsDialog = false;
            this.activeTab = this.billingTabIndex;
        },
        async purchase() {
            const tier = this.selectedTierObject;
            if (!tier) {
                return;
            }

            this.purchasing = true;
            this.rememberBillingBaseline();
            const response = await this.$store.dispatch('validateIq/purchaseValidations', {
                validations: tier.validations,
                autoRenew: this.purchaseAutoRenewEffective
            });
            this.purchasing = false;

            if (response && response.data && response.data.success && response.data.checkoutUrl) {
                window.location.href = response.data.checkoutUrl;
                return;
            }

            if (response && response.data && response.data.success) {
                // No Stripe URL: an internal test account, where the pack is added immediately at no charge.
                this.showSnackbar('Validations added. This is a test account, so nothing was charged.', 'success');
                await this.loadUsage();
                this.reloadInvoicesFromFirstPage();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Checkout Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not start checkout.'
            });
        },
        async managePaymentMethod() {
            this.openingPortal = true;
            const response = await this.$store.dispatch('validateIq/updatePaymentMethod');
            this.openingPortal = false;

            if (response && response.data && response.data.success && response.data.checkoutUrl) {
                window.location.href = response.data.checkoutUrl;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Payment Method Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not open payment method settings.'
            });
        },
        renewalTierPriceCents(validations) {
            const tier = this.plans.tiers.find(t => t.validations === validations);
            if (tier) {
                return tier.autoRenewPriceCents;
            }

            return validations === this.usage.renewalValidations ? this.usage.renewalPriceCents : null;
        },
        // The switch is driven by usage.autoRenewEnabled, so after any outcome that does not
        // change it (cancelled, failed, redirected) the switch is re-rendered to snap back.
        resetAutoRenewSwitch() {
            this.autoRenewSwitchKey++;
        },
        onAutoRenewSwitch(enabled) {
            this.requestAutoRenew(!!enabled, this.renewalTierSelection);
        },
        onRenewalTierChange(validations) {
            // While renewal is off the pack is only what turning it on would buy, so nothing is
            // charged or changed yet. While it is on, switching packs changes what gets charged,
            // so ask first (cancel snaps the selector back).
            if (this.usage.autoRenewEnabled && validations !== this.usage.renewalValidations) {
                this.requestRenewalPackChange(validations);
            }
        },
        packLabel(validations) {
            return validations ? `${this.formatNumber(validations)} validations` : 'Your renewal pack';
        },
        packPrice(validations) {
            const cents = this.renewalTierPriceCents(validations);
            return cents === null || cents === undefined ? '-' : this.formatCents(cents);
        },
        openConfirm(config) {
            this.autoRenewConfirm = {
                show: true,
                action: 'autoRenew',
                subtitle: '',
                icon: 'mdi-help-circle-outline',
                iconBg: 'primary',
                rows: [],
                notes: [],
                enabled: false,
                validations: null,
                ...config
            };
        },
        requestRenewalPackChange(validations) {
            const noneLeft = this.usage.availableValidations <= 0;
            const current = this.usage.renewalValidations;

            this.openConfirm({
                title: 'Change renewal pack?',
                subtitle: 'Automatic renewal will use the new pack.',
                icon: 'mdi-swap-horizontal',
                rows: [
                    { label: 'Current pack', value: current ? `${this.packLabel(current)} · ${this.packPrice(current)}` : '-' },
                    { label: 'New pack', value: `${this.packLabel(validations)} · ${this.packPrice(validations)}`, emphasis: true }
                ],
                notes: [
                    noneLeft
                        ? { type: 'warning', text: 'You have no validations left, so the new pack will be bought right away.' }
                        : { type: 'info', text: 'Takes effect at your next renewal. Nothing is charged now.' }
                ],
                confirmLabel: 'Change pack',
                enabled: true,
                validations
            });
        },
        // Every automatic renewal change is confirmed first; cancelling snaps the switch back.
        requestAutoRenew(enabled, validations) {
            if (enabled) {
                const notes = [];
                if (this.usage.availableValidations <= 0) {
                    notes.push({ type: 'warning', text: 'You have no validations left, so the pack will be bought right away.' });
                }
                notes.push({ type: 'info', text: 'Stripe keeps your card on file for renewals. If it has none yet, you will be taken to Stripe to buy this pack and save your card.' });

                this.openConfirm({
                    title: 'Turn on automatic renewal?',
                    subtitle: 'Keep validating without interruptions.',
                    icon: 'mdi-autorenew',
                    rows: [
                        { label: 'Renewal pack', value: this.packLabel(validations) },
                        { label: 'Charged each renewal', value: this.packPrice(validations), emphasis: true },
                        { label: 'Charged when', value: 'Your validations run out' }
                    ],
                    notes,
                    confirmLabel: 'Turn on',
                    enabled: true,
                    validations
                });
                return;
            }

            const notes = [{ type: 'info', text: 'You will need to buy more validations yourself when you run out.' }];
            if (this.liveSyncEnabled) {
                notes.push({ type: 'warning', text: 'Live Sync requires automatic renewal, so it will be turned off as well.' });
            }

            this.openConfirm({
                title: 'Turn off automatic renewal?',
                icon: 'mdi-autorenew-off',
                iconBg: 'grey darken-1',
                notes,
                confirmLabel: 'Turn off',
                enabled: false,
                validations: null
            });
        },
        requestPurchase() {
            const tier = this.selectedTierObject;
            if (!tier) {
                return;
            }

            const autoRenew = this.purchaseAutoRenewEffective;
            const alreadyOn = !!this.usage.autoRenewEnabled;
            const priceCents = this.tierPriceCents(tier);
            const price = this.formatCents(priceCents);
            const discounted = priceCents < tier.priceCents;
            const notes = [];
            if (autoRenew) {
                notes.push({ type: 'info', text: `When you run out, this same pack will be bought again automatically for ${this.formatCents(tier.autoRenewPriceCents)} with the card Stripe keeps on file.` });
            }
            notes.push({ type: 'info', text: 'You will be taken to Stripe to pay securely.' });

            this.openConfirm({
                action: 'purchase',
                title: 'Confirm purchase',
                subtitle: 'Review your order before paying.',
                icon: 'mdi-cart-outline',
                rows: [
                    { label: 'Validations', value: this.formatNumber(tier.validations) },
                    { label: 'Price per validation', value: this.formatPerValidation(tier, priceCents) },
                    ...(discounted
                        ? [
                            { label: 'List price', value: this.formatCents(tier.priceCents) },
                            { label: `Automatic renewal discount (${this.autoRenewDiscountPercent}%)`, value: `-${this.formatCents(tier.priceCents - priceCents)}` }
                        ]
                        : []),
                    { label: 'Turns on automatic renewal', value: alreadyOn ? 'Already on' : (autoRenew ? 'Yes' : 'No') },
                    { label: 'Total', value: price, emphasis: true }
                ],
                notes,
                confirmLabel: `Purchase for ${price}`,
                validations: tier.validations
            });
        },
        confirmAutoRenewChange() {
            const { action, enabled, validations } = this.autoRenewConfirm;
            this.autoRenewConfirm.show = false;

            if (action === 'purchase') {
                this.purchase();
                return;
            }

            this.applyAutoRenew(enabled, validations);
        },
        cancelAutoRenewConfirm() {
            this.autoRenewConfirm.show = false;

            // A cancelled purchase changed nothing, so the chosen pack and checkbox are left as they are.
            if (this.autoRenewConfirm.action === 'purchase') {
                return;
            }

            this.resetAutoRenewSwitch();
            this.syncBillingSelections();
        },
        async applyAutoRenew(enabled, validations) {
            this.togglingAutoRenew = true;
            this.rememberBillingBaseline();
            const response = await this.$store.dispatch('validateIq/setAutoRenew', {
                enabled,
                validations: enabled && validations ? validations : null
            });
            this.togglingAutoRenew = false;

            const data = response && response.data;

            if (data && data.success && data.checkoutUrl) {
                // No card on file at Stripe yet: the user buys the renewal pack on Stripe Checkout and
                // auto-renew turns on once payment completes.
                window.location.href = data.checkoutUrl;
                return;
            }

            if (data && data.success) {
                this.showSnackbar(
                    data.autoRenewEnabled ? 'Automatic renewal is on.' : 'Automatic renewal is off.',
                    'success'
                );
            } else {
                this.$swal({
                    icon: 'error',
                    title: 'Automatic Renewal Error',
                    text: data && (data.errorMessage || data.error)
                        ? (data.errorMessage || data.error)
                        : 'Could not update automatic renewal.'
                });
            }

            this.resetAutoRenewSwitch();
            await this.loadUsage();

            // Live Sync may have been switched off by the backend (it requires auto-renew).
            if (!this.liveSyncDirty) {
                await this.loadLiveSync();
            }
        },
        formatNumber(value) {
            return (value || 0).toLocaleString();
        },
        // Features that need automatic renewal show an X unless it is on, or ticked for the purchase being made.
        featureUnavailable(feature) {
            return !!feature.requiresAutoRenew && !this.purchaseAutoRenewEffective;
        },
        onPurchaseAutoRenewChange(value) {
            this.purchaseAutoRenew = !!value;
            this.purchaseAutoRenewTouched = true;
        },
        // What buying this pack would cost right now (discounted when auto-renew is, or is being, turned on).
        tierPriceCents(tier) {
            return this.purchaseDiscountActive ? tier.autoRenewPriceCents : tier.priceCents;
        },
        formatPerValidation(tier, priceCents) {
            if (!tier || !tier.validations) {
                return '-';
            }

            const cents = priceCents === undefined ? tier.priceCents : priceCents;
            return `$${(cents / 100 / tier.validations).toFixed(4)}`;
        },
        invoiceTypeLabel(type) {
            if (type === 'AUTO_RENEWAL') {
                return 'Automatic renewal';
            }
            if (type === 'ONE_TIME') {
                return 'Purchase';
            }
            return type || '-';
        },
        // Runs `action` immediately, unless there are unsaved Phone Properties/Formatting
        // changes - in that case it's held until the user confirms via the dialog, and
        // dropped entirely if they cancel.
        runWithUnsavedChangesGuard(action) {
            if (this.hasUnsavedChanges) {
                this.pendingConfirmedAction = action;
                this.showUnsavedChangesConfirm = true;
                return;
            }

            action();
        },
        confirmPendingAction() {
            this.showUnsavedChangesConfirm = false;
            const action = this.pendingConfirmedAction;
            this.pendingConfirmedAction = null;

            if (action) {
                action();
            }
        },
        cancelPendingAction() {
            this.showUnsavedChangesConfirm = false;
            this.pendingConfirmedAction = null;
        },
        async saveLiveSync() {
            this.savingLiveSync = true;
            const response = await this.$store.dispatch('validateIq/setLiveSync', this.liveSyncEnabled);
            this.savingLiveSync = false;

            if (response && response.status === 200 && response.data && !response.data.errorMessage) {
                this.liveSyncEnabled = !!response.data.enabled;
                this.savedLiveSyncEnabled = this.liveSyncEnabled;
                this.$swal({
                    icon: 'success',
                    title: this.liveSyncEnabled ? 'Continuous Monitoring Enabled' : 'Continuous Monitoring Disabled',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                this.loadUsage();
                return;
            }

            if (response && response.data && response.data.errorMessage) {
                this.liveSyncEnabled = !!response.data.enabled;
                this.savedLiveSyncEnabled = this.liveSyncEnabled;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: response && response.data && response.data.errorMessage
                    ? response.data.errorMessage
                    : 'Could not update Continuous Monitoring setting.'
            });
        },
        cancelLiveSync() {
            this.liveSyncEnabled = this.savedLiveSyncEnabled;
        },
        buildPhoneFormattingPayload() {
            return {
                formatNumbers: this.phoneFormatting.formatNumbers,
                addCountryCode: this.phoneFormatting.addCountryCodeEnabled,
                preserveExtension: this.phoneFormatting.preserveExtension
            };
        },
        async loadPhoneFormatting() {
            const response = await this.$store.dispatch('validateIq/getPhoneFormatting');

            if (response && response.status === 200 && response.data) {
                this.phoneFormatting = {
                    formatNumbers: !!response.data.formatNumbers,
                    addCountryCodeEnabled: !!response.data.addCountryCode,
                    preserveExtension: !!response.data.preserveExtension
                };
            }

            this.savedPhoneFormatting = JSON.parse(JSON.stringify(this.phoneFormatting));
        },
        async savePhoneFormattingOptions() {
            this.savingPhoneFormatting = true;
            const response = await this.$store.dispatch('validateIq/savePhoneFormatting', this.buildPhoneFormattingPayload());
            this.savingPhoneFormatting = false;

            if (response && response.status === 200 && response.data) {
                this.savedPhoneFormatting = JSON.parse(JSON.stringify(this.phoneFormatting));
                this.$swal({
                    icon: 'success',
                    title: 'Formatting Saved',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: 'Could not save phone formatting options.'
            });
        },
        cancelPhoneFormattingOptions() {
            this.phoneFormatting = JSON.parse(JSON.stringify(this.savedPhoneFormatting));
        },
        async loadPhoneProperties() {
            const response = await this.$store.dispatch('validateIq/getPhoneProperties');

            if (response && response.status === 200 && response.data) {
                this.phoneProperties = {
                    validatePhone: response.data.validatePhone !== false,
                    validateMobilePhone: response.data.validateMobilePhone !== false,
                    customProperties: Array.isArray(response.data.customProperties) ? response.data.customProperties.slice(0, 3) : []
                };
            }

            this.savedPhoneProperties = JSON.parse(JSON.stringify(this.phoneProperties));
        },
        buildPhonePropertiesPayload() {
            return {
                validatePhone: this.phoneProperties.validatePhone,
                validateMobilePhone: this.phoneProperties.validateMobilePhone,
                customProperties: this.phoneProperties.customProperties
            };
        },
        async savePhonePropertiesOptions() {
            this.savingPhoneProperties = true;
            const response = await this.$store.dispatch('validateIq/savePhoneProperties', this.buildPhonePropertiesPayload());
            this.savingPhoneProperties = false;

            if (response && response.status === 200 && response.data) {
                this.phoneProperties = {
                    validatePhone: response.data.validatePhone !== false,
                    validateMobilePhone: response.data.validateMobilePhone !== false,
                    customProperties: Array.isArray(response.data.customProperties) ? response.data.customProperties.slice(0, 3) : []
                };
                this.savedPhoneProperties = JSON.parse(JSON.stringify(this.phoneProperties));
                this.$swal({
                    icon: 'success',
                    title: 'Phone Properties Saved',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: 'Could not save phone property options.'
            });
        },
        cancelPhonePropertiesOptions() {
            this.phoneProperties = JSON.parse(JSON.stringify(this.savedPhoneProperties));
        },
        async openPropertyPicker() {
            this.selectedPropertyToAdd = null;
            this.showPropertyPicker = true;

            if (this.availableProperties.length === 0) {
                await this.loadAvailableProperties();
            }
        },
        async loadAvailableProperties() {
            this.loadingAvailableProperties = true;
            const response = await this.$store.dispatch('validateIq/getAvailablePhoneProperties');
            this.loadingAvailableProperties = false;

            if (response && response.status === 200 && Array.isArray(response.data)) {
                this.availableProperties = response.data;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Load Error',
                text: 'Could not load HubSpot contact properties.'
            });
        },
        addCustomProperty() {
            if (!this.selectedPropertyToAdd || this.phoneProperties.customProperties.length >= 3) {
                return;
            }

            if (!this.phoneProperties.customProperties.includes(this.selectedPropertyToAdd)) {
                this.phoneProperties.customProperties.push(this.selectedPropertyToAdd);
            }

            this.showPropertyPicker = false;
            this.selectedPropertyToAdd = null;
        },
        removeCustomProperty(propertyName) {
            this.phoneProperties.customProperties = this.phoneProperties.customProperties.filter(p => p !== propertyName);
        },
        propertyLabel(propertyName) {
            const found = this.availableProperties.find(p => p.name === propertyName);
            return found ? found.label : propertyName;
        },
        guardedValidateSingleContact() {
            this.runWithUnsavedChangesGuard(this.validateSingleContact);
        },
        extractHubSpotContactId(value) {
            if (!value) {
                return value;
            }

            const trimmed = value.trim();
            // HubSpot contact URLs look like
            // https://app.hubspot.com/contacts/<portalId>/record/<objectTypeId>/<contactId>[?query]
            const urlMatch = trimmed.match(/\/record\/[^/]+\/(\d+)/);
            return urlMatch ? urlMatch[1] : trimmed;
        },
        async validateSingleContact() {
            this.singleContactResult = null;
            const hsContactId = this.extractHubSpotContactId(this.singleContactId);
            if (!hsContactId) {
                this.$swal({
                    icon: 'warning',
                    title: 'Missing contact id',
                    text: 'Enter a HubSpot contact id or contact URL first.'
                });
                return;
            }

            this.validatingSingleContact = true;
            const response = await this.$store.dispatch('validateIq/validateContact', {
                hsContactId,
                phoneFormatting: this.buildPhoneFormattingPayload()
            });
            this.validatingSingleContact = false;

            if (response && response.status === 200 && response.data) {
                this.singleContactResult = response.data;
                return;
            }

            this.singleContactResult = {
                success: false,
                errorMessage: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Failed to validate contact.'
            };
        },
        async loadSegments() {
            this.loadingSegments = true;
            const response = await this.$store.dispatch('validateIq/getSegments');
            this.loadingSegments = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.segments = response.data.segments || [];
                return;
            }

            this.segments = [];
        },
        guardedOpenValidateContactsDialog() {
            this.runWithUnsavedChangesGuard(this.openValidateContactsDialog);
        },
        openValidateContactsDialog() {
            this.startError = null;
            this.validateScope = 'all';
            this.selectedSegmentId = null;
            this.estimate = null;
            this.showValidateContactsDialog = true;
        },
        closeValidateContactsDialog() {
            this.showValidateContactsDialog = false;
        },
        async loadEstimate() {
            this.loadingEstimate = true;
            const response = await this.$store.dispatch('validateIq/getEstimate', {
                scope: this.validateScope,
                hubSpotListId: this.validateScope === 'segment' ? this.selectedSegmentId : null
            });
            this.loadingEstimate = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.estimate = response.data;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Estimate Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not estimate validation count.'
            });
        },
        async startValidation() {
            this.startError = null;
            this.startingValidation = true;
            const response = this.validateScope === 'segment'
                ? await this.$store.dispatch('validateIq/validateSegmentContacts', {
                    listId: this.selectedSegmentId,
                    phoneFormatting: this.buildPhoneFormattingPayload()
                })
                : await this.$store.dispatch('validateIq/validateAllContacts', this.buildPhoneFormattingPayload());
            this.startingValidation = false;

            if (response && response.status === 202 && response.data && response.data.success) {
                this.showValidateContactsDialog = false;
                this.$swal({
                    icon: 'success',
                    title: 'Sync Started',
                    text: response.data.message || 'Validate IQ sync has been queued.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });

                await this.loadSyncDashboard();
                return;
            }

            if (response && response.status === 402) {
                this.startError = response.data && response.data.errorMessage
                    ? response.data.errorMessage
                    : 'No validations available. Buy validations or turn on automatic renewal to run this sync.';
                this.estimate = null;
                this.loadUsage();
                return;
            }

            if (response && response.status === 429) {
                this.$swal({
                    icon: 'info',
                    title: 'Sync In Progress',
                    text: response.data && response.data.errorMessage ? response.data.errorMessage : 'A sync is already in progress.'
                });
                await this.loadSyncDashboard();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Sync Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not start sync.'
            });
        },
        async loadInvoices() {
            this.loadingInvoices = true;
            const response = await this.$store.dispatch('validateIq/getInvoices', {
                page: this.invoicesOptions.page,
                pageSize: this.invoicesOptions.itemsPerPage
            });
            this.loadingInvoices = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.invoices = response.data.invoices || [];
                this.invoicesTotal = response.data.totalCount || 0;
                return;
            }

            this.invoices = [];
            this.invoicesTotal = 0;
        },
        // After a new purchase the newest invoice is on page 1; the options watcher does the load when the page changes.
        reloadInvoicesFromFirstPage() {
            if (this.invoicesOptions.page !== 1) {
                this.invoicesOptions = { ...this.invoicesOptions, page: 1 };
                return;
            }

            this.loadInvoices();
        },
        formatCents(cents) {
            return `$${((cents || 0) / 100).toFixed(2)}`;
        },
        async loadSyncDashboard() {
            this.loadingDashboard = true;
            const response = await this.$store.dispatch('validateIq/getSyncDashboard', 5);
            this.loadingDashboard = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.dashboard = {
                    activeRun: response.data.activeRun,
                    recentRuns: response.data.recentRuns || []
                };
                this.togglePolling();
                return;
            }

            this.dashboard = {
                activeRun: null,
                recentRuns: []
            };
        },
        togglePolling() {
            if (!this.activeRun) {
                this.stopPolling();
                return;
            }

            if (this.poller) {
                return;
            }

            this.poller = setInterval(async () => {
                await this.loadSyncDashboard();
            }, 10000);
        },
        stopPolling() {
            if (this.poller) {
                clearInterval(this.poller);
                this.poller = null;
            }
        },
        statusColor(status) {
            if (status === 'COMPLETED') {
                return 'success';
            }
            if (status === 'FAILED') {
                return 'error';
            }
            return 'warning';
        },
        statusLabel(status) {
            if (status === 'PARTIAL') {
                return 'Partial - ran out of validations';
            }

            return status;
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }

            return new Date(value).toLocaleString();
        }
    }
}
</script>

<style scoped>
.tier-card {
    cursor: pointer;
    border: 2px solid rgba(0, 0, 0, 0.12);
    transition: border-color 0.15s, box-shadow 0.15s;
}

.tier-card:hover,
.tier-card:focus {
    border-color: var(--v-primary-lighten2);
    outline: none;
}

.tier-card--selected,
.tier-card--selected:hover,
.tier-card--selected:focus {
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 1px var(--v-primary-base);
}
</style>



